import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Video, Download, RefreshCw, CheckCircle,
  ExternalLink, Upload, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VideoHistory from './VideoHistory';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PROMPT_EXAMPLES_SCENE = [
  'Confetti falls from above',
  'A dog runs in from the side',
  'Party atmosphere, colorful lights',
  'Zoom in slowly, cinematic',
];
const PROMPT_EXAMPLES_CAMERA = [
  'Zoom in',
  'Zoom out',
  'Camera moves upward',
  'Fixed camera position',
];

const AvatarMotionPage = () => {
  const navigate = useNavigate();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoName, setVideoName] = useState('');

  const [mode, setMode] = useState('std');
  const [orientation, setOrientation] = useState('video');
  const [keepSound, setKeepSound] = useState('yes');
  const [prompt, setPrompt] = useState('');

  const [status, setStatus] = useState('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [resultUrl, setResultUrl] = useState(null);
  const [recordId, setRecordId] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10 MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleVideoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100 MB'); return; }
    setVideoFile(file);
    setVideoName(file.name);
  };

  const startProgress = () => {
    setProgress(12);
    const steps = [
      { at: 8000, val: 25 }, { at: 20000, val: 40 },
      { at: 40000, val: 58 }, { at: 70000, val: 72 },
      { at: 100000, val: 83 }, { at: 140000, val: 91 },
    ];
    steps.forEach(s => setTimeout(() => setProgress(v => Math.max(v, s.val)), s.at));
  };

  const generate = async () => {
    const token = getToken();
    if (!token) { toast.error('Please sign in first'); return; }
    if (!imageFile) { toast.error('Please upload a character image'); return; }
    if (!videoFile) { toast.error('Please upload a motion reference video'); return; }

    setStatus('generating'); setStatusMsg('Uploading your files...'); setProgress(5);

    try {
      const formData = new FormData();
      formData.append('image_file', imageFile);
      formData.append('video_file', videoFile);
      formData.append('mode', mode);
      formData.append('character_orientation', orientation);
      formData.append('keep_original_sound', keepSound);
      formData.append('prompt', prompt);

      const res = await axios.post(`${API}/motion-studio/create`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(5 + Math.round((e.loaded / e.total) * 12));
        },
      });

      if (res.data.success && res.data.task_id) {
        setRecordId(res.data.record_id);
        setStatus('polling');
        setStatusMsg('Files uploaded — AI is transferring the motion...');
        startProgress();
        pollStatus(res.data.task_id, res.data.record_id);
      }
    } catch (e) {
      setStatus('error');
      setStatusMsg(e.response?.data?.detail || 'Generation failed. Please try again.');
    }
  };

  const pollStatus = (taskId, recId) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > 180) {
        clearInterval(interval); setStatus('error');
        setStatusMsg('Timed out. Please try again.'); return;
      }
      try {
        const res = await axios.get(`${API}/motion-studio/status/${taskId}`);
        const { status: s, video_url } = res.data;
        if (s === 'completed' && video_url) {
          clearInterval(interval); setProgress(100); setResultUrl(video_url); setStatus('done');
          toast.success('Your motion video is ready!');
          if (recId) {
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'completed', video_url },
              { headers: { Authorization: `Bearer ${getToken()}` } }
            ).catch(() => {});
          }
        } else if (s === 'failed') {
          clearInterval(interval); setStatus('error');
          setStatusMsg('Motion transfer failed. Check your files and try again.');
          if (recId) {
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'failed' },
              { headers: { Authorization: `Bearer ${getToken()}` } }
            ).catch(() => {});
          }
        } else {
          setStatusMsg(`Transferring motion... (${s})`);
          setProgress(v => Math.max(v, Math.min(92, 20 + attempts * 1.2)));
        }
      } catch (_) {}
    }, 5000);
  };

  const reset = () => {
    setStatus('idle'); setStatusMsg(''); setProgress(0); setResultUrl(null); setRecordId(null);
    setImageFile(null); setImagePreview(null); setVideoFile(null); setVideoName(''); setPrompt('');
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = resultUrl; a.download = 'motion-video.mp4'; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate('/creator-ai/heygen-hub')}
            className="flex items-center gap-1 text-gray-400 hover:text-gray-700 mb-2 text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-gray-900 leading-tight">Avatar Motion Studio</h1>
              <p className="text-xs text-gray-500 leading-tight">Upload image + motion video · Standard 300 / Pro 500 credits</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-4 space-y-4">

        {/* ══════════════════════════════════════════════════════ IDLE */}
        {status === 'idle' && (
          <>
            <VideoHistory videoType="avatar_motion" accentColor="amber" />

            {/* Steps */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { n: '1', t: 'Character', s: 'Upload photo' },
                { n: '2', t: 'Motion Video', s: 'Upload video' },
                { n: '3', t: 'Get Result', s: 'AI animates' },
              ].map(item => (
                <div key={item.n} className="bg-white rounded-2xl border border-gray-200 p-3 text-center">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-xs mx-auto mb-1.5">{item.n}</div>
                  <p className="text-xs font-semibold text-gray-800 leading-tight">{item.t}</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-tight">{item.s}</p>
                </div>
              ))}
            </div>

            {/* Key tip */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex gap-2.5">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-xs font-bold text-amber-800">Key tip for best results</p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  Match body type — <strong>half-body image + half-body video</strong> or <strong>full-body image + full-body video</strong>. Mismatching reduces quality.
                </p>
              </div>
            </div>

            {/* ── Step 1: Character Image ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <div>
                <p className="text-sm font-bold text-gray-900">Step 1 — Character Image</p>
                <p className="text-xs text-gray-400 mt-0.5">JPG or PNG · max 10 MB · whole body visible</p>
              </div>

              <input ref={imageInputRef} type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleImageFile} className="hidden" />
              <div
                onClick={() => imageInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden hover:border-amber-400 cursor-pointer transition-colors"
              >
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Character" className="w-full h-48 object-contain bg-gray-50" loading="lazy" decoding="async"/>
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs rounded-lg px-2 py-1">Tap to change</div>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Upload className="h-9 w-9 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-500">Tap to upload image</p>
                    <p className="text-xs text-gray-400 mt-1">JPG / PNG · max 10 MB</p>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                {[
                  'Full or upper body clearly visible',
                  'No extreme angles or occlusion',
                  'Realistic & stylised characters supported',
                ].map((t, i) => (
                  <p key={i} className="text-xs text-gray-500 flex gap-1.5"><span className="text-green-500 flex-shrink-0">✓</span><span>{t}</span></p>
                ))}
              </div>
            </div>

            {/* ── Step 2: Motion Reference Video ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <div>
                <p className="text-sm font-bold text-gray-900">Step 2 — Motion Reference Video</p>
                <p className="text-xs text-gray-400 mt-0.5">MP4 or MOV · max 100 MB · 3–30 seconds</p>
              </div>

              <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,.mp4,.mov" onChange={handleVideoFile} className="hidden" />
              <div
                onClick={() => videoInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl hover:border-amber-400 cursor-pointer transition-colors"
              >
                {videoFile ? (
                  <div className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Video className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{videoName}</p>
                      <p className="text-xs text-gray-400">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB · Tap to change</p>
                    </div>
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Video className="h-9 w-9 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-500">Tap to upload motion video</p>
                    <p className="text-xs text-gray-400 mt-1">MP4 / MOV · max 100 MB</p>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                {[
                  'Single continuous shot — no cuts',
                  'No camera movement in the video',
                  'Moderate speed, avoid very fast motion',
                  '3 to 30 seconds',
                  'One person (AI uses the largest if multiple)',
                ].map((t, i) => (
                  <p key={i} className="text-xs text-gray-500 flex gap-1.5"><span className="text-green-500 flex-shrink-0">✓</span><span>{t}</span></p>
                ))}
              </div>
            </div>

            {/* ── Step 3: Settings ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-5">
              <p className="text-sm font-bold text-gray-900">Step 3 — Settings</p>

              {/* Quality Mode */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Quality Mode</label>
                <select
                  value={mode}
                  onChange={e => setMode(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                <option value="std">Standard — 300 credits flat</option>
                  <option value="pro">Professional — 500 credits flat</option>
                </select>
              </div>

              {/* Character Orientation */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Character Orientation</label>
                <select
                  value={orientation}
                  onChange={e => setOrientation(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  <option value="video">Matches Video — Pose follows motion video (up to 30s)</option>
                  <option value="image">Matches Image — Keeps photo pose, enables camera control (up to 10s)</option>
                </select>
              </div>

              {/* Keep Sound */}
              <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-xl">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-700">Keep original audio</p>
                  <p className="text-xs text-gray-400 mt-0.5">Include sound from the reference video</p>
                </div>
                <button
                  onClick={() => setKeepSound(s => s === 'yes' ? 'no' : 'yes')}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${keepSound === 'yes' ? 'bg-amber-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${keepSound === 'yes' ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Prompt */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-600">Optional Prompt</p>
                <p className="text-xs text-gray-400">
                  {orientation === 'image' ? 'Control camera movement (e.g. zoom, pan)' : 'Add scene elements or background details'}
                </p>
                <input
                  type="text"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder={orientation === 'image' ? 'e.g. Zoom in slowly' : 'e.g. Confetti falls from above'}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <div className="flex flex-wrap gap-1.5">
                  {(orientation === 'image' ? PROMPT_EXAMPLES_CAMERA : PROMPT_EXAMPLES_SCENE).map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => setPrompt(ex)}
                      className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-2 py-1"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generate}
              disabled={!imageFile || !videoFile}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold py-4 rounded-2xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm shadow-lg"
            >
              <Zap className="h-4 w-4" /> Generate Motion Video · {mode === 'pro' ? '500' : '300'} credits
            </button>

            <p className="text-xs text-gray-400 text-center pb-4">
              Standard = 300 credits · Professional = 500 credits
            </p>
          </>
        )}

        {/* ══════════════════════════════════════════════════════ GENERATING */}
        {(status === 'generating' || status === 'polling') && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                <Video className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">Generating Your Motion Video</p>
                <p className="text-xs text-gray-500 mt-0.5 break-words">{statusMsg}</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-gray-400">
                <span>Transferring motion...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-800">⏱ Average: <strong>2–5 minutes</strong>. Keep this page open.</p>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════ ERROR */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl border border-red-200 p-5 text-center space-y-4">
            <p className="text-red-600 font-medium text-sm break-words">{statusMsg}</p>
            <button onClick={reset} className="bg-gray-900 text-white px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 mx-auto text-sm">
              <RefreshCw className="h-4 w-4" /> Try Again
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════ DONE */}
        {status === 'done' && resultUrl && (
          <div className="bg-white rounded-2xl border border-green-200 p-4 space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-bold text-sm">Your Motion Video is Ready!</span>
            </div>
            <div className="bg-black rounded-xl overflow-hidden">
              <video
                src={resultUrl} controls playsInline
                className="w-full max-h-72 sm:max-h-96"
                onError={() => toast.error('Preview failed — use Open button')}
              />
            </div>
            <div className="space-y-2.5">
              <button onClick={handleDownload}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm">
                <Download className="h-4 w-4" /> Download Video
              </button>
              <a href={resultUrl} target="_blank" rel="noreferrer"
                className="w-full border-2 border-amber-300 text-amber-700 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-amber-50 text-sm">
                <ExternalLink className="h-4 w-4" /> Open in Browser
              </a>
              <button onClick={reset}
                className="w-full border-2 border-gray-200 text-gray-600 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                <RefreshCw className="h-4 w-4" /> Create Another
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AvatarMotionPage;
